import { useCallback, useEffect } from 'react';
import {
  DashboardType,
  TypeOfUser,
  calendarDate,
  calendarFilter,
  getLanguagesInfo,
  getNotificationsCount,
  getPharmacistCalendar,
  getPharmacistFilterValues,
  getPharmacistPicture,
  getPharmacyCalendar,
  getPharmacyInfo,
  getTypeOfUser,
  getUserGeneral,
  profileUtils,
  setActiveDashboard,
  pharmacistActions,
  successOrSelector,
  resetStatus,
  getClientAdvertisements,
  getPharmacyBilling,
} from '@pharmaplan/common';
import { useLocation } from 'react-router-dom';
import { busyFlagKey, Constants } from '../helpers/Constants';
import { setPageTitle } from '../helpers/Functions';
import { useAppDispatch } from './useAppDispatch';
import { useAppSelector } from './useAppSelector';
import strings from '../localization';

import useInterval from './useInterval';
import useSelfService from './useSelfService';
import useProfessional from './Professional/useProfessional';

const { pharmacy } = TypeOfUser;
const intervalDelay = 4 * 60 * 1000;

const watchListActions = [
  pharmacistActions.watchlist,
  pharmacistActions.deleteWatchlist,
];

const useDashboard = () => {
  const dispatch = useAppDispatch();
  const location = useLocation();
  const { isSelfService } = useSelfService();

  const cDate = useAppSelector(calendarDate);
  const userType = useAppSelector(getTypeOfUser);
  const { selfUserType } = useProfessional();
  const filters = useAppSelector(calendarFilter);

  const watchListActionsSuccess = useAppSelector((state) =>
    successOrSelector(watchListActions, state));

  const { pathname } = location;
  const splitPath = pathname.split('/')[2];

  const { weekendVolume, weekVolume } = filters ?? {};
  const isWeekendVolumeString = typeof weekendVolume === Constants.string;
  const isweekVolumeString = typeof weekVolume === Constants.string;

  const isPharmacy = userType === pharmacy;

  const getUserBasedCalendar = useCallback(() => {
    if (isPharmacy) {
      dispatch(getPharmacyCalendar(cDate));
    } else {
      dispatch(
        getPharmacistCalendar({
          ...filters,
          weekendVolume: isWeekendVolumeString
            ? JSON.parse(weekendVolume)
            : weekendVolume,
          weekVolume: isweekVolumeString ? JSON.parse(weekVolume) : weekVolume,
          ...cDate,
        }),
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userType, filters, cDate, weekendVolume, weekVolume]);

  const getUserData = () => {
    if (isSelfService && isPharmacy) {
      dispatch(getPharmacyBilling());
    }

    if (isPharmacy) {
      dispatch(getPharmacyInfo());
    } else {
      dispatch(getPharmacistFilterValues());
      dispatch(getUserGeneral());
      dispatch(getPharmacistPicture());
    }
  };

  useEffect(() => {
    dispatch(setActiveDashboard(splitPath as DashboardType));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname]);

  useEffect(() => {
    getUserBasedCalendar();
  }, [getUserBasedCalendar]);

  useEffect(() => {
    // STARTUP API CALLS
    setPageTitle(strings.dashboard);
    getUserData();
    dispatch(profileUtils(userType));
    dispatch(
      getClientAdvertisements(
        selfUserType,
      ),
    );
    dispatch(getLanguagesInfo());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (watchListActionsSuccess) {
      getUserBasedCalendar();
      dispatch(resetStatus(watchListActions));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [watchListActionsSuccess]);

  const intervalCallback = () => {
    const busy = localStorage.getItem(busyFlagKey) === 'true';

    if (busy) {
      setTimeout(() =>
        intervalCallback, 2000);
    } else {
      localStorage.setItem(busyFlagKey, 'true');
      if (isPharmacy) {
        isSelfService && dispatch(getNotificationsCount());
        dispatch(getPharmacyCalendar(cDate)).finally(() => {
          localStorage.setItem(busyFlagKey, 'false');
        });
      } else {
        isSelfService && dispatch(getNotificationsCount());
        dispatch(
          getPharmacistCalendar({
            ...filters,
            weekendVolume: isWeekendVolumeString
              ? JSON.parse(weekendVolume)
              : weekendVolume,
            weekVolume: isweekVolumeString ? JSON.parse(weekVolume) : weekVolume,
            ...cDate,
          }),
        ).finally(() => {
          localStorage.setItem(busyFlagKey, 'false');
        });
      }
    }
  };

  useInterval(intervalCallback, intervalDelay, [cDate]);
};

export default useDashboard;
